import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Check, Trash2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = ['pending', 'approved', ''];

const adminPwd = () => encodeURIComponent(sessionStorage.getItem('adminPassword') || '');

const CommentRow = ({ comment, onApprove, onDelete }) => (
  <div className="border rounded-lg p-4 flex gap-4 justify-between" data-testid={`admin-comment-${comment.comment_id}`}>
    <div className="min-w-0">
      <div className="text-sm text-gray-500">
        <strong className="text-gray-900">{comment.display_name}</strong> · {comment.email} · {new Date(comment.created_at).toLocaleString()}
      </div>
      <div className="text-xs text-gray-400 mb-2">{comment.article_id} · <span data-testid={`admin-comment-status-${comment.comment_id}`}>{comment.status}</span></div>
      <p className="whitespace-pre-wrap break-words text-gray-800">{comment.body}</p>
    </div>
    <div className="flex flex-col gap-2 shrink-0">
      {comment.status !== 'approved' && (
        <Button size="sm" onClick={() => onApprove(comment.comment_id)} data-testid={`admin-comment-approve-${comment.comment_id}`}>
          <Check className="h-4 w-4 mr-1" /> Approve
        </Button>
      )}
      <Button size="sm" variant="destructive" onClick={() => onDelete(comment.comment_id)} data-testid={`admin-comment-delete-${comment.comment_id}`}>
        <Trash2 className="h-4 w-4 mr-1" /> Delete
      </Button>
    </div>
  </div>
);

export default function CommunityCommentsManager() {
  const [filter, setFilter] = useState('pending');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter ? `&status=${filter}` : '';
      const res = await axios.get(`${API}/community/admin/comments?password=${adminPwd()}${status}`);
      setComments(res.data);
    } catch (e) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error('Action failed'); }
  };
  const approve = (id) => act(() => axios.post(`${API}/community/admin/comments/${id}/approve?password=${adminPwd()}`), 'Comment approved');
  const remove = (id) => window.confirm('Delete this comment permanently?') &&
    act(() => axios.delete(`${API}/community/admin/comments/${id}?password=${adminPwd()}`), 'Comment deleted');

  return (
    <div className="bg-white rounded-xl shadow-lg p-6" data-testid="community-comments-manager">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Article Comments</h2>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <Button key={f || 'all'} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)} data-testid={`comments-filter-${f || 'all'}`}>
              {f ? f[0].toUpperCase() + f.slice(1) : 'All'}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={load} data-testid="comments-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>
      {loading ? <p className="text-gray-500">Loading…</p> : comments.length === 0 ? (
        <p className="text-gray-500" data-testid="comments-empty">No {filter} comments.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => <CommentRow key={c.comment_id} comment={c} onApprove={approve} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
